import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, SectionHeader } from "../components/Card";
import { Button } from "../components/Button";
import { Input } from "../components/Input";

export function ResetPasswordScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [refreshToken, setRefreshToken] = useState("");

  useEffect(() => {
    // Extract access token from URL hash (Supabase format)
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1));
    const queryAccessToken = searchParams.get("access_token");
    const queryRefreshToken = searchParams.get("refresh_token");
    const token = params.get("access_token") || queryAccessToken;
    const refresh = params.get("refresh_token") || queryRefreshToken;

    if (token && refresh) {
      setAccessToken(token);
      setRefreshToken(refresh);
    } else {
      setError("Invalid or expired reset link");
    }
  }, [searchParams]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/api/account/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password, access_token: accessToken, refresh_token: refreshToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to reset password");
      }

      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: "100vh",
      background: "var(--bg)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16
    }}>
      <Card style={{ maxWidth: 450, width: "100%", margin: 0 }}>
        <SectionHeader
          emoji="🔐"
          title="Reset Password"
          subtitle="Enter your new password below."
        />

        {success ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <h3 style={{
              fontFamily: "var(--font-display)",
              fontSize: 20,
              fontWeight: 700,
              color: "var(--text)",
              marginBottom: 8
            }}>
              Password Reset Successful
            </h3>
            <p style={{ fontSize: 14, color: "var(--text-muted)", lineHeight: 1.6, marginBottom: 20 }}>
              Your password has been updated. You can now log in with your new password.
            </p>
            <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
              Redirecting to login...
            </p>
          </div>
        ) : !accessToken ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
            <h3 style={{
              fontFamily: "var(--font-display)",
              fontSize: 20,
              fontWeight: 700,
              color: "var(--text)",
              marginBottom: 8
            }}>
              Invalid Reset Link
            </h3>
            <p style={{ fontSize: 14, color: "var(--text-muted)", lineHeight: 1.6, marginBottom: 20 }}>
              This password reset link is invalid or has expired.
              Please request a new one.
            </p>
            <div style={{ display: "flex", gap: 12, flexDirection: "column" }}>
              <Button
                label="Request New Link"
                onClick={() => navigate("/forgot-password")}
                icon="📧"
              />
              <button
                onClick={() => navigate("/login")}
                style={{
                  padding: "12px 20px",
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: 14,
                  background: "transparent",
                  color: "var(--text-muted)",
                  border: "1px solid var(--border)",
                  cursor: "pointer"
                }}
              >
                Back to Login
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ marginTop: 20 }}>
            <div style={{ display: "grid", gap: 16 }}>
              <Input
                label="New Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 8 characters"
                required
                disabled={loading}
              />

              <Input
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                required
                disabled={loading}
              />
            </div>

            {error && (
              <div style={{
                marginTop: 12,
                padding: "12px 16px",
                background: "var(--status-danger-bg)",
                border: "1px solid var(--status-danger-border)",
                borderRadius: 8,
                color: "var(--status-danger)",
                fontSize: 13
              }}>
                {error}
              </div>
            )}

            <div style={{ marginTop: 20, display: "flex", gap: 12, flexDirection: "column" }}>
              <Button
                type="submit"
                label={loading ? "Updating..." : "Reset Password"}
                loading={loading}
                icon="🔒"
              />

              <button
                type="button"
                onClick={() => navigate("/login")}
                disabled={loading}
                style={{
                  padding: "12px 20px",
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: 14,
                  background: "transparent",
                  color: "var(--text-muted)",
                  border: "1px solid var(--border)",
                  cursor: loading ? "not-allowed" : "pointer"
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
